import "@src/components/table/table.css";
import { Button, DatePicker, Dropdown, Input, Layout, Menu, Pagination, Select } from "antd";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretLeft, faCaretRight, faSearch } from "@fortawesome/free-solid-svg-icons";
import { CaretDownFilled } from "@ant-design/icons";

// The toolbar pieces of Business Analysis / Mobile Services, for the full-screen grids.

const outlined = {
    height: '40px',
    borderRadius: '14px',
    border: '2px solid #e5e7eb',
    color: '#374151',
    fontWeight: 500,
    padding: '0 24px',
    background: '#ffffff',
};

/** The search box on the left of the toolbar */
export const GridSearch = ({ value, onChange, placeholder }) => (
    <Input
        placeholder={placeholder}
        suffix={<FontAwesomeIcon icon={faSearch} style={{ color: '#0091ae' }} />}
        className="generic-input-control"
        style={{ width: 'min(500px, 100%)', maxWidth: '500px', height: '40px', borderRadius: '14px', border: '2px solid #e5e7eb' }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        allowClear
    />
);

/** ◀ October 2026 ▶ (click the month to pick one) and This Month */
export const MonthPicker = ({ month, onChange }) => (
    <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
            <span className="sicon-bound" onClick={() => onChange(month.subtract(1, 'month'))}><FontAwesomeIcon icon={faCaretLeft} style={{ fontSize: '16px' }} /></span>
            <div style={{ display: 'inline-block', position: 'relative', height: '40px' }}>
                <Input style={{ width: '220px', textAlign: 'center', padding: '4px 20px', height: '40px', borderRadius: '14px' }} value={month.format('MMMM YYYY')} readOnly />
                <div style={{ position: 'absolute', top: '0', width: '100%', zIndex: '1', opacity: '0' }}>
                    <DatePicker picker="month" className="generic-input-control" onChange={(value) => value && onChange(value.startOf('month'))}
                        disabledDate={(d) => d && d.isAfter(dayjs(), 'month')} />
                </div>
            </div>
            <span className="sicon-bound" onClick={() => { if (!month.isSame(dayjs(), 'month')) onChange(month.add(1, 'month')); }}><FontAwesomeIcon style={{ fontSize: '16px' }} icon={faCaretRight} /></span>
        </div>
        <Button className="sm-btn" style={{ height: '40px', width: '100px' }} onClick={() => onChange(dayjs().startOf('month'))}>This Month</Button>
    </div>
);

/** A select styled like the toolbar buttons */
export const GridSelect = ({ value, onChange, options, width = 240 }) => (
    <Select
        className="custom-select fsg-select"
        suffixIcon={<CaretDownFilled />}
        style={{ width }}
        value={value}
        onChange={onChange}
        options={options}
    />
);

/** Table Actions ▾ - [{ key, label, onClick, disabled }] */
export const TableActions = ({ actions }) => (
    <Dropdown overlay={
        <Menu>
            {actions.map((action) => (
                <Menu.Item key={action.key} className="popoverdataitem" disabled={action.disabled} onClick={action.onClick}>{action.label}</Menu.Item>
            ))}
        </Menu>
    } trigger={['click']}>
        <Button className="sm-btn" style={outlined}>Table Actions <CaretDownFilled /></Button>
    </Dropdown>
);

/**
 * The module grid around a table (pagination={false} on the table) with the page bar under it,
 * as on Mobile Services: the server sends the page and the total.
 */
export const PagedGrid = ({ children, page, pageSize, total, onChange, className = "" }) => (
    <div className={`tableView ${className}`}>
        <Layout className="bg-white all-module-grid">
            <Layout.Content className="site-layout">
                {children}
                {total > 0 && (
                    <div className="table-pagination-bar">
                        <Pagination
                            current={page}
                            pageSize={pageSize}
                            total={total}
                            showSizeChanger={true}
                            pageSizeOptions={["10", "20", "50", "100"]}
                            onChange={(nextPage, nextSize) => onChange(nextSize !== pageSize ? 1 : nextPage, nextSize)}
                        />
                    </div>
                )}
            </Layout.Content>
        </Layout>
    </div>
);

/** What's typed, a moment after typing stops - for searches sent to the server */
export const useDebounced = (value, ms = 350) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), ms);
        return () => clearTimeout(timer);
    }, [value, ms]);
    return debounced;
};
